import { useMemo, useState } from 'react';
import { Plus, Search, Link2, SearchX } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { StatusBadge } from '@/components/shared/status-badge';
import { formatCurrency } from '@/lib/utils';
import { useLocationStore } from '@/stores/location-store';
import { useRPPaymentLinks } from '@/hooks/use-receive-payments';
import { useSaleTransactions } from '@/hooks/use-sale-transactions';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { usePermissions } from '@/hooks/use-permissions';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

export function PayByLinkTab({ onCreatePaymentLink }: { onCreatePaymentLink: () => void }) {
  const canCreate = usePermissions().has('rp.links.create');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const { selectedLocationId } = useLocationStore();

  const { data, isLoading } = useRPPaymentLinks({
    status: statusFilter || undefined,
    locationId: selectedLocationId || undefined,
  });
  const links = data?.data ?? [];

  const { data: salesData } = useSaleTransactions();
  const saleStatusById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of salesData?.data ?? []) {
      map[s.id] = s.status;
    }
    return map;
  }, [salesData]);

  const filteredLinks = useMemo(() => {
    if (!search.trim()) return links;
    const query = search.toLowerCase();
    return links.filter(
      (link) =>
        link.linkNumber.toLowerCase().includes(query) ||
        link.customer?.name?.toLowerCase().includes(query) ||
        link.customer?.company?.toLowerCase().includes(query),
    );
  }, [links, search]);

  const total = filteredLinks.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(
    () => filteredLinks.slice((page - 1) * perPage, page * perPage),
    [filteredLinks, page, perPage],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="paid">Paid</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <div className="relative flex-1 lg:max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by customer name or link #..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2 lg:ml-auto">
            <button
              onClick={onCreatePaymentLink}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 lg:flex-none"
            >
              <Plus className="h-4 w-4" />
              Create Payment Link
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading payment links...</div>
      ) : filteredLinks.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
          {search ? (
            <EmptyState icon={SearchX} title="No matching payment links" description="Try a different search term." />
          ) : (
            <EmptyState
              icon={Link2}
              title="No payment links yet"
              description="Create your first payment link to start collecting payments."
            />
          )}
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-border bg-white overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="border-b border-border bg-muted/50 rounded-t-lg">
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground rounded-tl-lg">
                    Link #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Customer</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Expiry Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground rounded-tr-lg">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((link) => (
                  <tr key={link.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-medium text-primary">{link.linkNumber}</span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {link.customer?.name || 'Unknown'}
                      {link.customer?.company && (
                        <span className="ml-1 text-xs text-muted-foreground">({link.customer.company})</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-right">{formatCurrency(link.amount)}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {link.dueDate ? new Date(link.dueDate).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        status={
                          link.saleTransactionId && saleStatusById[link.saleTransactionId]
                            ? saleStatusById[link.saleTransactionId]
                            : link.status
                        }
                      />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-muted-foreground">—</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DataTablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            perPage={perPage}
            onPageChange={setPage}
            perPageOptions={PER_PAGE_OPTIONS}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        </>
      )}
    </div>
  );
}
